import { useEffect, useRef } from "react";
import { MessageEvent } from "../BookingMessageTypes";
import { Language } from "../../../language/English";

interface BookingMessageActionsProps {
    event: MessageEvent;
    onSeeAllMessage: (event: MessageEvent) => void;
    hideSeeAllMessage?: boolean;
    getBusinessEditLink: (businessId: number | null) => string;
    getBusinessPublicProfileLink: (businessUrl: string | null, businessId: number | null) => string;
    getBookingDetailsLink: (bookingId: number | null) => string;
    getBusinessBookingsLink: (businessId: number | null) => string;
    openActionId: string | null;
    setOpenActionId: (id: string | null) => void;
}

const BookingMessageActions = ({
    event,
    onSeeAllMessage,
    hideSeeAllMessage = false,
    getBusinessEditLink,
    getBusinessPublicProfileLink,
    getBookingDetailsLink,
    getBusinessBookingsLink,
    openActionId,
    setOpenActionId,
}: BookingMessageActionsProps) => {
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const isOpen = openActionId === event.id;

    useEffect(() => {
        const handleOutsideClick = (mouseEvent: MouseEvent) => {
            if (!isOpen) {
                return;
            }

            const targetNode = mouseEvent.target as Node;
            if (dropdownRef.current && !dropdownRef.current.contains(targetNode)) {
                setOpenActionId(null);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, setOpenActionId]);

    const handleToggleMenu = () => {
        if (isOpen) {
            setOpenActionId(null);
            return;
        }

        setOpenActionId(event.id);
    };

    const handleMenuActionClick = () => {
        setOpenActionId(null);
    };

    return (
        <div className="booking-message-actions booking-message-inline-actions">
            <div
                ref={dropdownRef}
                className={`booking-message-action-dropdown ${isOpen ? "is-open" : ""}`}
            >
                <button
                    type="button"
                    className="booking-message-action-link booking-message-action-trigger"
                    onClick={handleToggleMenu}
                >
                    <i className="fas fa-ellipsis-h" />
                    <span>{Language.ACTIONS}</span>
                </button>
                {isOpen ? (
                <div className="booking-message-action-menu">
                    <a
                        className={`booking-message-action-link ${event.businessId ? "" : "is-disabled"}`}
                        href={getBusinessEditLink(event.businessId)}
                        aria-disabled={!event.businessId}
                        target="_blank"
                        rel="noreferrer"
                        onClick={handleMenuActionClick}
                    >
                        <i className="fas fa-edit" /> {Language.EDIT_BUSINESS}
                    </a>
                    <a
                        className={`booking-message-action-link ${event.businessUrl || event.businessId ? "" : "is-disabled"}`}
                        href={getBusinessPublicProfileLink(event.businessUrl, event.businessId)}
                        target="_blank"
                        rel="noreferrer"
                        aria-disabled={!event.businessUrl && !event.businessId}
                        onClick={handleMenuActionClick}
                    >
                        <i className="fas fa-external-link-alt" /> {Language.PUBLIC_PROFILE}
                    </a>
                    <a
                        className={`booking-message-action-link ${event.bookingId ? "" : "is-disabled"}`}
                        href={getBookingDetailsLink(event.bookingId)}
                        aria-disabled={!event.bookingId}
                        target="_blank"
                        rel="noreferrer"
                        onClick={handleMenuActionClick}
                    >
                        <i className="fas fa-calendar-check" /> {Language.BOOKING_DETAILS}
                    </a>
                    <a
                        className={`booking-message-action-link ${event.businessId ? "" : "is-disabled"}`}
                        href={getBusinessBookingsLink(event.businessId)}
                        aria-disabled={!event.businessId}
                        target="_blank"
                        rel="noreferrer"
                        onClick={handleMenuActionClick}
                    >
                        <i className="fas fa-list" /> {Language.SEE_ALL_BOOKINGS_FOR_THIS_BUSINESS}
                    </a>
                </div>
                ) : null}
            </div>
            {!hideSeeAllMessage ? (
                <button
                    type="button"
                    className="booking-message-action-link booking-message-action-button"
                    onClick={() => onSeeAllMessage(event)}
                    disabled={!event.phoneNumber || event.phoneNumber === "-"}
                >
                    {Language.SEE_ALL_MESSAGES}
                </button>
            ) : null}
        </div>
    );
};

export default BookingMessageActions;
